import { memo, useId } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { FormGroup } from './style/FormGroup';
import { Input } from './style/Input';
import { useComputeInputFieldHandleChange } from './InputField.hooks';
import { InputFieldProps } from './InputField.types';
import FieldFeedback from '../FieldFeedback/FieldFeedback';
import LabelInput from '../Label/Label';
import { fieldStateReducer } from '../Form.helpers';

const InputField = ({
  type = 'text',
  name,
  label,
  id,
  hidden,
  transformError,
  className,
  inputComponent: InputComponent = Input,
  autoComplete = 'off',
  maxLength,
  disabled,
  placeholder,
  filterValue,
  initialFieldState,
  isFieldErrorAlert,
  inputComponentExtraProps,
  handleBlur,
  required,
  noFeedback,
  feedbackOnlyIfMessage,
  onChangeCallback,
}: InputFieldProps) => {
  const { control } = useFormContext();
  const {
    fieldState: controllerFieldState,
    field: { onChange, onBlur, ...rest },
  } = useController({
    name,
    control,
  });

  const compId = useId();
  const domId = id || compId;

  const { fieldState, messageProps } = fieldStateReducer(
    controllerFieldState,
    isFieldErrorAlert,
    transformError,
    initialFieldState,
  );

  const handleChange = useComputeInputFieldHandleChange(
    onChange,
    filterValue,
    onChangeCallback,
  );

  const handleOnBlur = (e: FocusEvent) => {
    onBlur();
    if (handleBlur) {
      handleBlur(e);
    }
  };
  return (
    <FormGroup fieldState={fieldState} hidden={hidden} className={className}>
      <InputComponent
        type={type}
        required={required}
        id={domId}
        autoComplete={autoComplete}
        maxLength={maxLength}
        disabled={disabled}
        onBlur={handleOnBlur}
        onChange={handleChange}
        {...rest}
        {...inputComponentExtraProps}
        //safari only
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder}
      />
      {label && <LabelInput domId={domId} label={label} />}
      <FieldFeedback
        noFeedback={noFeedback}
        feedbackOnlyIfMessage={feedbackOnlyIfMessage}
        messageProps={messageProps}
      />
    </FormGroup>
  );
};

export default memo(InputField) as typeof InputField;
